import React from 'react';
import { Region } from '../../types/orderTypes';
import { ScannerMode, ScanningState } from './scanningTypes';

export interface ScannerFeedbackBoxProps {
  scanningState: ScanningState;
  loading: boolean;
  scannerReady: boolean;
  shelfId: string;
  orderIdForShelfId: string;
  setShelfId: boolean;
  unknownOrder: boolean;
  differentRegionScanned?: Region;
  storedInWarehouse: boolean;
  manualInputOpen: boolean;
  orderIdForRescan?: string;
  scannerMode: ScannerMode;
}

function getDisplayShelfId(shelfId: string): string {
  const idx = shelfId.indexOf(':');
  if (idx > -1) {
    return shelfId.substr(idx + 1);
  }
  return shelfId;
}

export const ScannerFeedbackBox = ({
  scanningState,
  loading,
  scannerReady,
  shelfId,
  orderIdForShelfId,
  setShelfId,
  unknownOrder,
  differentRegionScanned,
  storedInWarehouse,
  manualInputOpen,
  orderIdForRescan,
  scannerMode,
}: ScannerFeedbackBoxProps) => {
  if (!scannerReady && !manualInputOpen && scannerMode === 'laser') {
    return (
      <div className="scannerInput__feedback scannerInput__feedback--lostFocus">
        <span>NOT READY, TAP TO ACTIVATE</span>
      </div>
    );
  } else if (loading) {
    return (
      <div className="scannerInput__feedback scannerInput__feedback--loading" />
    );
  } else if (scanningState === 'delivered-or-canceled') {
    return (
      <div className="scannerInput__feedback scannerInput__feedback--delivered-or-cancelled">
        <span>{`Order ${orderIdForShelfId} is already`}</span>
        <span>delivered or cancelled.</span>
        <span>Please contact dispatchers!</span>
      </div>
    );
  } else if (scanningState === 'confirm-shelf') {
    if (!!orderIdForRescan) {
      return (
        <div className="scannerInput__feedback scannerInput__feedback--rescanWarning">
          <span>Not a shelf!</span>
          <span>{`Scan shelf ${shelfId} to confirm previous order`}</span>
          <span>Scan order again to continue</span>
          <span>{`Order ${orderIdForRescan}`}</span>
        </div>
      );
    }
    return (
      <div className="scannerInput__feedback scannerInput__feedback--confirmShelf">
        <span>SCAN SHELF</span>
        <span className="shelfId">{shelfId}</span>
        <span>{`Order ${orderIdForShelfId}`}</span>
      </div>
    );
  } else if (scanningState === 'confirm-warehouse') {
    if (!!orderIdForRescan) {
      return (
        <div className="scannerInput__feedback scannerInput__feedback--rescanWarning">
          <span>Not the warehouse!</span>
          <span>{`Scan warehouse to confirm previous order`}</span>
          <span>Scan order again to continue</span>
          <span>{`Order ${orderIdForRescan}`}</span>
        </div>
      );
    }
    return (
      <div className="scannerInput__feedback scannerInput__feedback--confirmWarehouse">
        <span>{`Order ${orderIdForShelfId}`}</span>
        <span>{unknownOrder ? 'is unknown' : 'has no shelf id'}</span>
        <span>Store in warehouse?</span>
      </div>
    );
  } else if (scanningState === 'confirm-package') {
    if (setShelfId) {
      const idClassName = shelfId.startsWith('to:') ? 'linehaulId' : 'shelfId';
      return (
        <div className="scannerInput__feedback scannerInput__feedback--success">
          <span className={idClassName}>{`${getDisplayShelfId(
            shelfId
          )}\u2713`}</span>
          <span>{`Order ${orderIdForShelfId}`}</span>
        </div>
      );
    }
    if (storedInWarehouse) {
      return (
        <div className="scannerInput__feedback scannerInput__feedback--success">
          <span>Warehouse</span>
          <span className="warehouse">{`\u2713`}</span>
          <span>{`Order ${orderIdForShelfId}`}</span>
        </div>
      );
    }
  } else if (scanningState === 'confirm-linehaul') {
    if (!!orderIdForRescan) {
      return (
        <div className="scannerInput__feedback scannerInput__feedback--rescanWarning">
          <span>Not a line-haul code!</span>
          <span>{`Scan line-haul ${differentRegionScanned} to confirm previous order`}</span>
          <span>Scan order again to continue</span>
          <span>{`Order ${orderIdForRescan}`}</span>
        </div>
      );
    }
    return (
      <div className="scannerInput__feedback scannerInput__feedback--invalid-region">
        <span>{`Order ${orderIdForShelfId} is for`}</span>
        <span className="order-region">{differentRegionScanned}</span>
        <span>Scan line-haul code</span>
      </div>
    );
  }
  return (
    <div className="scannerInput__feedback">
      <span>SCAN PACKAGE</span>
    </div>
  );
};
